import React from 'react';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { Tooltip } from '@mui/material';
import { theme } from '../../../theme/theme';
import { getGemColumnValue } from '../../../utils/gem.utils';
import { IGem } from '../../../redux/gem/gemSlice';
import { GemClarity } from '../../../utils/enums/gem-clarity.enum';
import { GemLocation } from '../../../utils/enums/gem-location.enum';
import { Intensity } from '../../../utils/enums/intensity.enum';
import { colors } from '../../V1/FilterComponent/GemFilterModal/Colors';
import { GemColor } from '../../../utils/enums/gem-color.enum';

interface GemDetailsProps {
  gem: IGem | null;
  expandedRowColumns: string[];
  expandedRowValues: string[];
}

function GemDetails({ expandedRowColumns, expandedRowValues, gem }: GemDetailsProps) {
  const gemId = gem?.is_scanned;
  const filteredexpandedRowColumns = [
    ...expandedRowColumns.filter((column) => column !== 'Location' || gem?.is_scanned),
  ];
  const filteredexpandedRowValues = [...expandedRowValues.filter((value) => value !== 'location' || gem?.is_scanned)];

  const renderColorDot = (value: any) => {
    const colorMatch = colors.find((color) => color.name.toLowerCase() === value.toLowerCase());
    return colorMatch ? (
      <Box
        component="span"
        sx={{
          display: 'inline-block',
          width: '10px',
          height: '10px',
          borderRadius: '50%',
          backgroundColor: colorMatch.code,
          marginRight: '8px',
          verticalAlign: 'middle',
          border: colorMatch.name === GemColor.White ? `1px solid ${theme.palette.common.white}` : 'none',
        }}
      />
    ) : null;
  };

  const renderSpecialDescriptor = (value: any) => (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 5px',
        borderRadius: '5px',
        border: `1px solid ${theme.palette.shades.grayBorder}`,
        backgroundColor: 'rgba(51, 72, 255, 0.04)',
        width: 'fit-content',
        maxWidth: '95%',
        overflow: 'hidden',
      }}
    >
      <Tooltip title={value || 'N/A'}>
        <Typography
          variant="textBase500"
          color="black.dark"
          sx={{
            wordBreak: 'break-word',
            whiteSpace: 'normal',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: 'block',
            maxWidth: '100%',
          }}
        >
          {value || 'N/A'}
        </Typography>
      </Tooltip>
    </Box>
  );

  const renderListedDate = (value: any) => {
    let formattedDate = 'N/A';

    if (value) {
      const date = new Date(value);
      const localeDate = date.toLocaleDateString('en-GB');

      const isAlreadyFormatted = /^\d{2}\/\d{2}\/\d{4}$/.test(value);

      formattedDate = isAlreadyFormatted ? value : localeDate;
    }

    return (
      <Typography
        variant="textBase500"
        sx={{
          display: 'inline-flexS',
          borderRadius: '5px',
          whiteSpace: 'nowrap',
          maxWidth: '100px',
          wordBreak: 'break-word',
          color: 'inherit',
        }}
      >
        {formattedDate}
      </Typography>
    );
  };

  const gemWithFallbacks: Partial<IGem> = {
    ...gem,
    secondaryColor: gem?.secondaryColor ?? 'N/A',
    intensity: gem?.intensity ? (gem.intensity as Intensity) : 'N/A',
    clarity: (gem?.clarity as GemClarity) ?? 'N/A',
    specialDescriptor: gem?.specialDescriptor ?? 'N/A',
    dimensions:
      gem && gem.length != null && gem.width != null && gem.depth != null
        ? `${gem.length.toFixed(2)} x ${gem.width.toFixed(2)} x ${gem.depth.toFixed(2)}`
        : (gem?.dimensions ?? 'N/A'),
    location: (gem?.location as GemLocation) ?? 'N/A',
  };
  const colorColumns = ['Primary Color', 'Secondary Color', 'Intensity'];

  if (gem) {
    return (
      <Grid
        p={gem.is_scanned ? 3 : 0}
        display="flex"
        flexDirection="column"
        borderRadius="0 0px 4px 4px"
        border={gem.is_scanned ? `1px solid ${theme.palette.shades.grayBorder}` : 'none'}
        gap={3}
        borderTop={gemId ? 'none' : undefined}
      >
        <Grid
          display="flex"
          flexDirection={{ xs: 'column', sm: 'column', md: 'row' }}
          gap={3}
          width="100%"
          alignItems="center"
        >
          <Grid container rowGap={3}>
            {filteredexpandedRowColumns.map((column, i) => {
              const value = getGemColumnValue(gemWithFallbacks as IGem, filteredexpandedRowValues[i]);

              const isColorColumn = colorColumns.includes(column);

              const existingColorColsInRow = filteredexpandedRowColumns.filter((col) => colorColumns.includes(col));

              const indexInColorSubset = existingColorColsInRow.indexOf(column);
              const isFirstColorCol = indexInColorSubset === 0;
              const isLastColorCol = indexInColorSubset === 2;

              return (
                <Grid
                  item
                  key={column}
                  display="flex"
                  flexDirection="column"
                  xs={12}
                  sm={6}
                  md={4}
                  lg={1.6}
                  xl={2}
                  sx={{
                    flex: '0 0 auto',
                    maxWidth: 'fit-content',
                    backgroundColor: isColorColumn ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
                    padding: isColorColumn ? '4px 0 4px 8px' : '0',
                    borderTopLeftRadius: isColorColumn && isFirstColorCol ? '8px' : 0,
                    borderTopRightRadius: isColorColumn && isLastColorCol ? '8px' : 0,
                    borderBottomLeftRadius: isColorColumn && isFirstColorCol ? '8px' : 0,
                    borderBottomRightRadius: isColorColumn && isLastColorCol ? '8px' : 0,
                    marginRight: column === 'Intensity' ? '12px' : 0,
                  }}
                >
                  {(() => {
                    if (column === 'Special Color Descriptor') {
                      return renderSpecialDescriptor(value);
                    }
                    if (column === 'Listing Date') {
                      if (value) {
                        renderListedDate(value);
                      } else {
                        return renderListedDate(gem.createdAt);
                      }
                    }
                    if (column === 'Weight(ct)') {
                      if (typeof value === 'number') {
                        return (
                          <Tooltip title={value || 'N/A'}>
                            <Typography
                              variant="textBase500"
                              sx={{
                                display: 'inline-flexS',
                                borderRadius: '0 5px 0 0',
                                maxWidth: '100px',
                                color: 'inherit',
                                backgroundColor: 'transparent',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {value.toFixed(2) || 'NA'}
                            </Typography>
                          </Tooltip>
                        );
                      }
                      return 'NA';
                    }
                    return (
                      <Tooltip title={value || 'N/A'}>
                        <Typography
                          variant="textBase500"
                          sx={{
                            display: 'inline-flexS',
                            borderRadius: '0 5px 0 0',
                            maxWidth: '100px',
                            color: 'inherit',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {(column === 'Primary Color' || column === 'Secondary Color') && renderColorDot(value)}
                          {value}
                        </Typography>
                      </Tooltip>
                    );
                  })()}

                  <Typography
                    variant="textXS"
                    sx={{
                      display: 'inline-flex',
                      whiteSpace: 'normal',
                      maxWidth: '100px',
                      borderRadius: '0 5px 5px 0',
                      color: theme.palette.shades.lightDark,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      lineHeight: 1.2,
                    }}
                  >
                    {column}
                  </Typography>
                </Grid>
              );
            })}
          </Grid>
        </Grid>
      </Grid>
    );
  }
  return null;
}

export default GemDetails;
